import axios from "axios";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import AlertComponent from "../../components/AlertComponent";
import Modal from "../../components/Modal";
import { EmptyState, PageHeader, Panel } from "../../components/Page";
import StatusBadge from "../../components/StatusBadge";
import { Context } from "../../Reducers/Store";
import { apiUrl, getPayload } from "../../services/apiConfig";
import { diasSemana, metodosHttp, tiposAgendamento, tiposParametro } from "../../utils/options";
import { errorMessage, formatDateTime, formatDuration, fromDateTimeLocalInput, methodClass, toDateTimeLocalInput } from "../../utils/formatters";
import { buildCronExpression, defaultCronEditor, describeCronExpression, parseCronExpression } from "../../utils/cronExpression";

const emptyJob = {
  nome: "",
  descricao: "",
  url: "",
  metodoHttp: "Get",
  tipoAgendamento: "Diario",
  horario: "08:00",
  fusoHorario: "America/Sao_Paulo",
  autenticacaoId: "",
  grupoComunicacaoErroId: "",
  expressaoCron: "",
  diaDaSemana: "Monday",
  diaDoMes: 1,
  iniciarEm: "",
  corpo: "",
  tipoConteudo: "application/json",
  validarRespostaJson: false,
  caminhoFlagSucesso: "success",
  caminhoMensagemResposta: "message",
  timeoutSegundos: 120,
  maximoTentativas: 1,
  intervaloTentativasSegundos: 30,
  parametros: [],
};

function fromJob(job) {
  if (!job) return emptyJob;

  return {
    nome: job.nome ?? "",
    descricao: job.descricao ?? "",
    url: job.url ?? "",
    metodoHttp: job.metodoHttp ?? "Get",
    tipoAgendamento: job.tipoAgendamento ?? "Diario",
    horario: String(job.horario ?? "08:00").slice(0, 5),
    fusoHorario: job.fusoHorario ?? "America/Sao_Paulo",
    autenticacaoId: job.autenticacaoId ?? "",
    grupoComunicacaoErroId: job.grupoComunicacaoErroId ?? "",
    expressaoCron: job.expressaoCron ?? "",
    diaDaSemana: job.diaDaSemana ?? "Monday",
    diaDoMes: job.diaDoMes ?? 1,
    iniciarEm: toDateTimeLocalInput(job.iniciarEm, job.fusoHorario ?? "America/Sao_Paulo"),
    corpo: job.corpo ?? "",
    tipoConteudo: job.tipoConteudo ?? "application/json",
    validarRespostaJson: Boolean(job.validarRespostaJson),
    caminhoFlagSucesso: job.caminhoFlagSucesso ?? "success",
    caminhoMensagemResposta: job.caminhoMensagemResposta ?? "message",
    timeoutSegundos: job.timeoutSegundos ?? 120,
    maximoTentativas: job.maximoTentativas ?? 1,
    intervaloTentativasSegundos: job.intervaloTentativasSegundos ?? 30,
    parametros: (job.parametros ?? []).map((parametro) => ({
      nome: parametro.nome,
      valor: parametro.valor ?? "",
      tipo: parametro.tipo,
      secreto: Boolean(parametro.secreto),
    })),
  };
}

function buildPayload(form) {
  return {
    nome: form.nome,
    descricao: form.descricao || null,
    url: form.url,
    metodoHttp: form.metodoHttp,
    tipoAgendamento: form.tipoAgendamento,
    horario: `${String(form.horario || "08:00").slice(0, 5)}:00`,
    fusoHorario: form.fusoHorario || "America/Sao_Paulo",
    autenticacaoId: form.autenticacaoId ? Number(form.autenticacaoId) : null,
    grupoComunicacaoErroId: form.grupoComunicacaoErroId ? Number(form.grupoComunicacaoErroId) : null,
    expressaoCron: form.tipoAgendamento === "Cron" ? form.expressaoCron || null : null,
    diaDaSemana: ["Semanal", "Quinzenal"].includes(form.tipoAgendamento) ? form.diaDaSemana || null : null,
    diaDoMes: form.tipoAgendamento === "Mensal" ? Number(form.diaDoMes || 1) : null,
    iniciarEm: form.iniciarEm ? fromDateTimeLocalInput(form.iniciarEm, form.fusoHorario || "America/Sao_Paulo") : null,
    corpo: form.corpo || null,
    tipoConteudo: form.tipoConteudo || "application/json",
    validarRespostaJson: Boolean(form.validarRespostaJson),
    caminhoFlagSucesso: form.validarRespostaJson ? form.caminhoFlagSucesso || null : null,
    caminhoMensagemResposta: form.validarRespostaJson ? form.caminhoMensagemResposta || null : null,
    timeoutSegundos: Number(form.timeoutSegundos || 120),
    maximoTentativas: Number(form.maximoTentativas || 1),
    intervaloTentativasSegundos: Number(form.intervaloTentativasSegundos || 30),
    parametros: form.parametros
      .filter((parametro) => parametro.nome?.trim())
      .map((parametro) => ({
        nome: parametro.nome,
        valor: parametro.valor || null,
        tipo: parametro.tipo,
        secreto: Boolean(parametro.secreto),
      })),
  };
}

function scheduleDetail(job) {
  if (job.tipoAgendamento === "Cron") {
    return describeCronExpression(job.expressaoCron);
  }

  return String(job.horario || "").slice(0, 5);
}

function IconButton({ title, onClick, disabled, children, tone = "slate" }) {
  const colors = {
    slate: "text-slate-600 hover:bg-slate-100 hover:text-slate-950",
    red: "text-red-600 hover:bg-red-50 hover:text-red-700",
    emerald: "text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800",
  }[tone];

  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-md transition disabled:cursor-not-allowed disabled:text-slate-300 ${colors}`}
    >
      {children}
    </button>
  );
}

function CronExpressionEditor({ value, onChange }) {
  const [editor, setEditor] = useState(() => parseCronExpression(value));

  useEffect(() => {
    setEditor(parseCronExpression(value));
  }, [value]);

  const updateEditor = (changes) => {
    const next = { ...editor, ...changes };
    const expression = buildCronExpression(next);
    setEditor({ ...next, customExpression: expression });
    onChange(expression);
  };

  const changeMode = (mode) => {
    const next = {
      ...editor,
      mode,
      customExpression: mode === "custom" ? value || editor.customExpression || buildCronExpression(defaultCronEditor) : editor.customExpression,
    };
    const expression = buildCronExpression(next);
    setEditor({ ...next, customExpression: expression });
    onChange(expression);
  };

  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Frequencia</label>
          <select value={editor.mode} onChange={(event) => changeMode(event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100">
            <option value="minutes">A cada X minutos</option>
            <option value="hours">A cada X horas</option>
            <option value="daily">Uma vez por dia</option>
            <option value="custom">Cron manual</option>
          </select>
        </div>

        {editor.mode === "minutes" && (
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Intervalo em minutos</label>
              <input type="number" min="1" max="59" value={editor.intervalMinutes} onChange={(event) => updateEditor({ intervalMinutes: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Minuto inicial</label>
              <input type="number" min="0" max="59" value={editor.startMinute} onChange={(event) => updateEditor({ startMinute: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
            </div>
          </div>
        )}

        {editor.mode === "hours" && (
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Intervalo em horas</label>
              <input type="number" min="1" max="23" value={editor.intervalHours} onChange={(event) => updateEditor({ intervalHours: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Minuto da hora</label>
              <input type="number" min="0" max="59" value={editor.hourMinute} onChange={(event) => updateEditor({ hourMinute: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
            </div>
          </div>
        )}

        {editor.mode === "daily" && (
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Horario</label>
            <input type="time" value={editor.dailyTime} onChange={(event) => updateEditor({ dailyTime: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
          </div>
        )}

        {editor.mode === "custom" && (
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Expressao cron</label>
            <input value={editor.customExpression} onChange={(event) => updateEditor({ customExpression: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" placeholder="0 0/30 * * * ?" required />
          </div>
        )}
      </div>

      {editor.mode !== "custom" && (
        <div className="mt-4">
          <label className="mb-1 block text-sm font-medium text-slate-700">Expressao cron gerada</label>
          <input value={value || buildCronExpression(editor)} readOnly className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm text-slate-600 outline-none" />
        </div>
      )}
    </div>
  );
}

function JobForm({ initialJob, autenticacoes, gruposComunicacaoErro, onCancel, onSave }) {
  const [form, setForm] = useState(() => fromJob(initialJob));

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const setTipoAgendamento = (value) => {
    setForm((current) => ({
      ...current,
      tipoAgendamento: value,
      expressaoCron: value === "Cron" && !current.expressaoCron ? buildCronExpression(defaultCronEditor) : current.expressaoCron,
    }));
  };

  const setParametro = (index, field, value) => {
    setForm((current) => ({
      ...current,
      parametros: current.parametros.map((parametro, idx) => (idx === index ? { ...parametro, [field]: value } : parametro)),
    }));
  };

  const addParametro = () => {
    setForm((current) => ({
      ...current,
      parametros: [...current.parametros, { nome: "", valor: "", tipo: "QueryString", secreto: false }],
    }));
  };

  const removeParametro = (index) => {
    setForm((current) => ({
      ...current,
      parametros: current.parametros.filter((_, idx) => idx !== index),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave(buildPayload(form));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Nome</label>
          <input value={form.nome} onChange={(event) => setField("nome", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" required />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">URL</label>
          <input value={form.url} onChange={(event) => setField("url", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" placeholder="https://..." required />
        </div>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Descrição</label>
        <input value={form.descricao} onChange={(event) => setField("descricao", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Método</label>
          <select value={form.metodoHttp} onChange={(event) => setField("metodoHttp", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100">
            {metodosHttp.map((metodo) => <option key={metodo} value={metodo}>{metodo}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Autenticação</label>
          <select value={form.autenticacaoId} onChange={(event) => setField("autenticacaoId", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100">
            <option value="">Sem autenticação</option>
            {autenticacoes.map((autenticacao) => <option key={autenticacao.id} value={autenticacao.id}>{autenticacao.descricao}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Grupo de falha</label>
          <select value={form.grupoComunicacaoErroId} onChange={(event) => setField("grupoComunicacaoErroId", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100">
            <option value="">Sem notificação</option>
            {gruposComunicacaoErro.map((grupo) => <option key={grupo.id} value={grupo.id}>{grupo.nome}{grupo.ativo ? "" : " (inativo)"}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Content-Type</label>
          <input value={form.tipoConteudo} onChange={(event) => setField("tipoConteudo", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Agendamento</label>
          <select value={form.tipoAgendamento} onChange={(event) => setTipoAgendamento(event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100">
            {tiposAgendamento.map((tipo) => <option key={tipo.value} value={tipo.value}>{tipo.label}</option>)}
          </select>
        </div>
        {form.tipoAgendamento !== "Cron" && (
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Horário</label>
            <input type="time" value={form.horario} onChange={(event) => setField("horario", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" required />
          </div>
        )}
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Fuso horário</label>
          <input value={form.fusoHorario} onChange={(event) => setField("fusoHorario", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Iniciar em</label>
          <input type="datetime-local" value={form.iniciarEm} onChange={(event) => setField("iniciarEm", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
        </div>
      </div>

      {["Semanal", "Quinzenal"].includes(form.tipoAgendamento) && (
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Dia da semana</label>
          <select value={form.diaDaSemana} onChange={(event) => setField("diaDaSemana", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100">
            {diasSemana.map((dia) => <option key={dia.value} value={dia.value}>{dia.label}</option>)}
          </select>
        </div>
      )}

      {form.tipoAgendamento === "Mensal" && (
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Dia do mês</label>
          <input type="number" min="1" max="31" value={form.diaDoMes} onChange={(event) => setField("diaDoMes", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" required />
        </div>
      )}

      {form.tipoAgendamento === "Cron" && (
        <CronExpressionEditor value={form.expressaoCron} onChange={(value) => setField("expressaoCron", value)} />
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Timeout (s)</label>
          <input type="number" min="1" value={form.timeoutSegundos} onChange={(event) => setField("timeoutSegundos", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Tentativas</label>
          <input type="number" min="1" value={form.maximoTentativas} onChange={(event) => setField("maximoTentativas", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Intervalo tentativas (s)</label>
          <input type="number" min="1" value={form.intervaloTentativasSegundos} onChange={(event) => setField("intervaloTentativasSegundos", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
        </div>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Corpo da requisição</label>
        <textarea value={form.corpo} onChange={(event) => setField("corpo", event.target.value)} rows={5} className="w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" placeholder='{"ano": {today.Year}, "mes": {today.Month}, "dia": {today.Day}}' />
      </div>

      <div className="rounded-lg border border-slate-200 p-4">
        <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
          <input type="checkbox" checked={form.validarRespostaJson} onChange={(event) => setField("validarRespostaJson", event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
          Validar retorno JSON
        </label>
        <p className="mt-1 text-xs text-slate-500">Depois de validar o HTTP status, o agendador confere a flag de sucesso configurada.</p>

        {form.validarRespostaJson && (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Campo flag de sucesso</label>
              <input value={form.caminhoFlagSucesso} onChange={(event) => setField("caminhoFlagSucesso", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" placeholder="success" required={form.validarRespostaJson} />
              <p className="mt-1 text-xs text-slate-500">Aceita caminho simples ou aninhado, como success ou data.success.</p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Campo mensagem</label>
              <input value={form.caminhoMensagemResposta} onChange={(event) => setField("caminhoMensagemResposta", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" placeholder="message" />
              <p className="mt-1 text-xs text-slate-500">Usado como detalhe quando a flag vier false ou invalida.</p>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-200">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-900">Parâmetros</h3>
          <button type="button" onClick={addParametro} className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
            </svg>
            Adicionar
          </button>
        </div>
        <div className="space-y-3 p-4">
          {form.parametros.length === 0 && <p className="text-sm text-slate-500">Nenhum parâmetro configurado.</p>}
          {form.parametros.map((parametro, index) => (
            <div key={index} className="grid gap-3 rounded-md border border-slate-200 p-3 lg:grid-cols-[1fr_1fr_150px_92px_40px] lg:items-center">
              <input value={parametro.nome} onChange={(event) => setParametro(index, "nome", event.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" placeholder="Nome" />
              <input value={parametro.valor} onChange={(event) => setParametro(index, "valor", event.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" placeholder="Valor" />
              <select value={parametro.tipo} onChange={(event) => setParametro(index, "tipo", event.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100">
                {tiposParametro.map((tipo) => <option key={tipo.value} value={tipo.value}>{tipo.label}</option>)}
              </select>
              <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={parametro.secreto} onChange={(event) => setParametro(index, "secreto", event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
                Secreto
              </label>
              <IconButton title="Remover parâmetro" onClick={() => removeParametro(index)} tone="red">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18 18 6M6 6l12 12" />
                </svg>
              </IconButton>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" onClick={onCancel} className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
          Cancelar
        </button>
        <button type="submit" className="rounded-md bg-violet-700 px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-violet-900/20 transition hover:bg-violet-800">
          Salvar job
        </button>
      </div>
    </form>
  );
}

function ExecucoesJobModal({ job, onClose }) {
  const [execucoes, setExecucoes] = useState([]);
  const [erro, setErro] = useState("");

  const carregarExecucoes = useCallback(() => {
    setErro("");
    axios
      .get(apiUrl(`/jobs/${job.id}/execucoes`), { params: { limite: 10 } })
      .then((response) => setExecucoes(getPayload(response) || []))
      .catch((error) => setErro(errorMessage(error, "Não foi possível carregar as execuções.")));
  }, [job.id]);

  useEffect(() => {
    carregarExecucoes();
  }, [carregarExecucoes]);

  useEffect(() => {
    if (!execucoes.some((execucao) => execucao.status === "EmExecucao")) return undefined;

    const interval = window.setInterval(carregarExecucoes, 3000);
    return () => window.clearInterval(interval);
  }, [carregarExecucoes, execucoes]);

  return (
    <Modal title={`Execuções de ${job.nome}`} subtitle={job.url} onClose={onClose} width="max-w-5xl">
      {erro && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{erro}</div>}
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full min-w-[820px] text-left">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Início</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Origem</th>
              <th className="px-4 py-3">HTTP</th>
              <th className="px-4 py-3">Duração</th>
              <th className="px-4 py-3">Resumo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {execucoes.map((execucao) => (
              <tr key={execucao.id}>
                <td className="px-4 py-3 text-sm text-slate-700">{formatDateTime(execucao.iniciadaEm)}</td>
                <td className="px-4 py-3"><StatusBadge value={execucao.status} /></td>
                <td className="px-4 py-3 text-sm text-slate-700">{execucao.origem}</td>
                <td className="px-4 py-3 text-sm text-slate-700">{execucao.codigoHttp ?? "-"}</td>
                <td className="px-4 py-3 text-sm text-slate-700">{formatDuration(execucao.duracaoMs)}</td>
                <td className="max-w-md px-4 py-3 text-sm text-slate-600">
                  <span className="line-clamp-2">{execucao.mensagemErro || execucao.resumoResposta || "-"}</span>
                </td>
              </tr>
            ))}
            {execucoes.length === 0 && (
              <tr>
                <td colSpan={6}>
                  <EmptyState title="Nenhuma execução encontrada." />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

export default function JobsPage() {
  const [state] = useContext(Context);
  const isAdmin = state.profile?.perfil === "Administrador";
  const [jobs, setJobs] = useState([]);
  const [execucoesRecentes, setExecucoesRecentes] = useState([]);
  const [autenticacoes, setAutenticacoes] = useState([]);
  const [gruposComunicacaoErro, setGruposComunicacaoErro] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("Ativos");
  const [editingJob, setEditingJob] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [historyJob, setHistoryJob] = useState(null);
  const [erro, setErro] = useState("");

  const carregar = useCallback(() => {
    setErro("");
    const calls = [
      axios.get(apiUrl("/jobs")),
      axios.get(apiUrl("/execucoes/recentes"), { params: { limite: 100 }, metadata: { ignoreLoading: true } }),
    ];
    if (isAdmin) {
      calls.push(axios.get(apiUrl("/autenticacoes"), { metadata: { ignoreLoading: true } }));
      calls.push(axios.get(apiUrl("/grupos-comunicacao-erro"), { metadata: { ignoreLoading: true } }));
    }

    Promise.all(calls)
      .then((responses) => {
        setJobs(getPayload(responses[0]) || []);
        setExecucoesRecentes(getPayload(responses[1]) || []);
        if (isAdmin) {
          setAutenticacoes(getPayload(responses[2]) || []);
          setGruposComunicacaoErro(getPayload(responses[3]) || []);
        }
      })
      .catch((error) => setErro(errorMessage(error, "Não foi possível carregar os jobs.")));
  }, [isAdmin]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!execucoesRecentes.some((execucao) => execucao.status === "EmExecucao")) return undefined;

    const interval = window.setInterval(carregar, 3000);
    return () => window.clearInterval(interval);
  }, [carregar, execucoesRecentes]);

  const filteredJobs = useMemo(() => {
    const term = filter.trim().toLowerCase();

    return jobs.filter((job) => {
      if (statusFilter === "Ativos" && !job.ativo) return false;
      if (statusFilter === "Inativos" && job.ativo) return false;

      if (!term) return true;

      return [job.nome, job.descricao, job.url, job.metodoHttp, job.tipoAgendamento, job.autenticacaoDescricao, job.grupoComunicacaoErroNome]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [filter, jobs, statusFilter]);

  const jobsEmExecucao = useMemo(() => {
    return new Set(
      execucoesRecentes
        .filter((execucao) => execucao.status === "EmExecucao")
        .map((execucao) => execucao.jobAgendadoId)
    );
  }, [execucoesRecentes]);

  const openCreate = () => {
    setEditingJob(null);
    setShowForm(true);
  };

  const openEdit = (job) => {
    setEditingJob(job);
    setShowForm(true);
  };

  const saveJob = (payload) => {
    const request = editingJob
      ? axios.put(apiUrl(`/jobs/${editingJob.id}`), payload)
      : axios.post(apiUrl("/jobs"), payload);

    request
      .then(() => {
        AlertComponent("Pronto", editingJob ? "Job atualizado com sucesso." : "Job cadastrado com sucesso.");
        setShowForm(false);
        setEditingJob(null);
        carregar();
      })
      .catch((error) => AlertComponent("Erro", errorMessage(error, "Não foi possível salvar o job."), { icon: "error" }));
  };

  const toggleStatus = (job) => {
    axios
      .patch(apiUrl(`/jobs/${job.id}/status`), { ativo: !job.ativo })
      .then(() => carregar())
      .catch((error) => AlertComponent("Erro", errorMessage(error, "Não foi possível alterar o status."), { icon: "error" }));
  };

  const executeJob = (job) => {
    axios
      .post(apiUrl(`/jobs/${job.id}/executar`))
      .then((response) => {
        const execucao = getPayload(response);
        if (execucao) {
          setExecucoesRecentes((current) => [
            execucao,
            ...current.filter((item) => item.id !== execucao.id),
          ].slice(0, 100));
        }

        AlertComponent("Job disparado", "A execucao foi iniciada em background.");
      })
      .catch((error) => AlertComponent("Erro", errorMessage(error, "Não foi possível executar o job."), { icon: "error" }));
  };

  const inativarJob = (job) => {
    AlertComponent(
      "Inativar job",
      `Deseja inativar o job "${job.nome}"?`,
      { icon: "warning", showCancelButton: true, confirmButtonText: "Inativar", cancelButtonText: "Cancelar" },
      (result) => {
        if (!result.isConfirmed) return;

        axios
          .patch(apiUrl(`/jobs/${job.id}/status`), { ativo: false })
          .then(() => {
            AlertComponent("Inativado", "Job inativado com sucesso.");
            carregar();
          })
          .catch((error) => AlertComponent("Erro", errorMessage(error, "Não foi possível inativar o job."), { icon: "error" }));
      },
      false
    );
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Jobs agendados"
        subtitle="Cadastro, execução manual e acompanhamento dos endpoints orquestrados."
        actions={
          isAdmin && (
            <button type="button" onClick={openCreate} className="inline-flex items-center gap-2 rounded-md bg-violet-700 px-3 py-2 text-sm font-semibold text-white shadow-sm shadow-violet-900/20 transition hover:bg-violet-800">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
              </svg>
              Novo job
            </button>
          )
        }
      />

      {erro && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{erro}</div>}

      <Panel
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
            >
              <option value="Ativos">Ativos</option>
              <option value="Inativos">Inativos</option>
              <option value="Todos">Todos</option>
            </select>
            <div className="relative w-full sm:w-80">
              <input
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                placeholder="Buscar job"
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 pl-9 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
              />
              <svg className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
              </svg>
            </div>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1080px] text-left">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Job</th>
                <th className="px-4 py-3">Método</th>
                <th className="px-4 py-3">Agendamento</th>
                <th className="px-4 py-3">Autenticação</th>
                <th className="px-4 py-3">Falhas</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredJobs.map((job) => {
                const emExecucao = jobsEmExecucao.has(job.id);

                return (
                <tr key={job.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="max-w-sm truncate text-sm font-semibold text-slate-900">{job.nome}</p>
                    <p className="max-w-sm truncate text-xs text-slate-500">{job.url}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${methodClass(job.metodoHttp)}`}>{job.metodoHttp}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">
                    <div>{job.tipoAgendamento}</div>
                    <div className="text-xs text-slate-500">{scheduleDetail(job)}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{job.autenticacaoDescricao || "-"}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{job.grupoComunicacaoErroNome || "-"}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      <StatusBadge value={job.ativo ? "Ativo" : "Inativo"} />
                      {emExecucao && <StatusBadge value="EmExecucao" />}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      {job.ativo && (
                        <>
                          <IconButton title={emExecucao ? "Execucao em andamento" : "Executar agora"} onClick={() => executeJob(job)} disabled={!job.podeExecutarManualmente || emExecucao} tone="emerald">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m8 5 11 7-11 7V5Z" />
                            </svg>
                          </IconButton>
                          {isAdmin && (
                            <IconButton title="Editar" onClick={() => openEdit(job)}>
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m16.86 4.49 2.65 2.65M5 19l4.5-1 9.9-9.9a1.88 1.88 0 0 0-2.65-2.65l-9.9 9.9L5 19Z" />
                              </svg>
                            </IconButton>
                          )}
                        </>
                      )}
                      <IconButton title="Histórico" onClick={() => setHistoryJob(job)}>
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </IconButton>
                      {isAdmin && (
                        job.ativo ? (
                          <IconButton title="Inativar" onClick={() => inativarJob(job)} tone="red">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v9m6.36-6.36a9 9 0 1 1-12.72 0" />
                            </svg>
                          </IconButton>
                        ) : (
                          <IconButton title="Reativar" onClick={() => toggleStatus(job)} tone="emerald">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12a8 8 0 0 1 13.66-5.66L20 8m0 0V3m0 5h-5M20 12a8 8 0 0 1-13.66 5.66L4 16m0 0v5m0-5h5" />
                            </svg>
                          </IconButton>
                        )
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
              {filteredJobs.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <EmptyState title="Nenhum job encontrado." subtitle={isAdmin ? "Cadastre um job ou ajuste o filtro." : "Você ainda não possui jobs liberados."} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      {showForm && (
        <Modal title={editingJob ? "Editar job" : "Novo job"} subtitle="Configure chamada, agendamento, autenticação e parâmetros." onClose={() => setShowForm(false)} width="max-w-6xl">
          <JobForm initialJob={editingJob} autenticacoes={autenticacoes} gruposComunicacaoErro={gruposComunicacaoErro} onCancel={() => setShowForm(false)} onSave={saveJob} />
        </Modal>
      )}

      {historyJob && <ExecucoesJobModal job={historyJob} onClose={() => setHistoryJob(null)} />}
    </div>
  );
}
